import React, { useState, useEffect, useImperativeHandle, useRef, useCallback, useMemo } from "react";
import RadioButtonGroup from "../../elements/radio-button-group";
import { generateReactKey } from "../../utils/reactKey";
import Icon from "../../elements/icon";
import Heading from "../../elements/heading";
import api from "../../utils/api";
import { connect } from "react-redux";
import { selectUserState, staffInfoSelector } from "../../selectors/application";
import { shippingInfoSelector } from "../../selectors/gears-checkout";
import { S_MARKET, webStorage } from "../../utils/webStorage";
import "./style.scss";
import AccountRemoveAddressModal from "../account-remove-address-modal";
import ShippingInfoForm from "../shipping-info-form";
import { isLoggedIn } from "../../services/siteService";
import AddressInformation from "../address-information";
import { SuperButton } from "../../elements";
const ShippingInfo = React.forwardRef(({ fields, user, addressList, loadAddressList, addAddress, updateAddress, removeAddress, visible, shippingFormData, staffInfo }, ref) => {
  const { headingType, heading, addressUnsavedMessage, addressMissingMessage, removeAddressModal, ctaNameChange, ctaTypeChange, colorSchemeChange, iconNameChange } = fields;
  const componentRoot = useRef();
  const addAddressRef = useRef();
  const market = webStorage.get(S_MARKET);
  const editAddressRef = useRef(null);
  const [defaultAddress, setDefaultAddress] = useState(null);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [addressToRemove, setAddressToRemove] = useState(null);
  const [showRemoveAddressModal, setShowRemoveAddressModal] = useState(null);
  const [editAddress, setEditAddress] = useState(null);
  const [addressEditing, setAddressEditing] = useState(false);
  const [formError, setFormError] = useState(null);
  useImperativeHandle(ref, () => ({
    validateForm() {
      if (isLoggedIn() && availableAddressList.length > 0) {
        if (editAddress) {
          editAddressRef.current.setError(addressUnsavedMessage.value);
          editAddressRef.current.getEl().scrollIntoView({ behavior: "smooth" });
          return {
            isValid: false,
            formData: null
          };
        } else if (selectedAddress) {
          if (selectedAddress.address) {
            return {
              isValid: true,
              formData: selectedAddress.address
            };
          } else {
            addAddressRef.current.setError(addressUnsavedMessage.value);
            addAddressRef.current.getEl().scrollIntoView({ behavior: "smooth" });
            return {
              isValid: false,
              formData: null
            };
          }
        } else {
          setFormError(addressMissingMessage.value);
          componentRoot.current.scrollIntoView({ behavior: "smooth" });
          return {
            isValid: false,
            formData: null
          };
        }
      } else {
        return addAddressRef.current.validateForm();
      }
    }
  }));
  useEffect(() => {
    if (addAddressRef.current) {
      setDefaultAddress({
        firstName: shippingFormData?.firstName || user?.firstName || "",
        lastName: shippingFormData?.lastName || user?.lastName || "",
        street: shippingFormData?.street || "",
        apartment: shippingFormData?.apartment || "",
        postalCode: shippingFormData?.postalCode || "",
        city: shippingFormData?.city || ""
      });
    }
  }, [user, shippingFormData, addAddressRef]);
  const availableAddressList = useMemo(() => {
    let _availableAddressList = addressList.filter(x => !x.address || x.address.countryCode === market.countryCode);
    if (_availableAddressList.length === 1 && _availableAddressList[0].value === "new") {
      _availableAddressList = [];
    }
    return _availableAddressList;
  }, [addressList]);
  useEffect(() => {
    if (availableAddressList.length > 0) {
      let _selectedAddress;
      if (selectedAddress) {
        if (selectedAddress.address) {
          _selectedAddress = availableAddressList.find(x => x.value === selectedAddress.address?.addressId);
        } else {
          _selectedAddress = availableAddressList.find(x => x.isNew);
          if (!_selectedAddress) {
            _selectedAddress = availableAddressList.find(x => x.address?.defaultFlag == "1");
          }
        }
      }
      if (!_selectedAddress) {
        if (shippingFormData?.addressId) {
          _selectedAddress = availableAddressList.find(x => x.value === shippingFormData.addressId);
        } else {
          _selectedAddress = availableAddressList.find(x => x.address?.defaultFlag == "1");
          if (!_selectedAddress) {
            _selectedAddress = availableAddressList[0];
          }
        }
      }
      setSelectedAddress(_selectedAddress);
    }
  }, [availableAddressList, shippingFormData]);
  const onAddressChange = address => {
    if (selectedAddress !== address) {
      setSelectedAddress(address);
      if (editAddress && editAddress !== address) {
        setEditAddress(null);
      }
      if (address.value !== "new") {
        setAddressEditing(false);
      } else {
        setDefaultAddress({
          firstName: user?.firstName || "",
          lastName: user?.lastName || "",
          street: null,
          apartment: null,
          postalCode: null,
          city: null
        });
      }
    }
  };
  const formatSavedAddress = address => {
    let name = `${address?.firstName || ""} ${address?.lastName || ""}`;
    let street = `${address?.street || ""} ${address?.apartment || ""}`;
    let zipCity = `${address?.postalCode || ""} ${address?.city || ""}`;
    return [name, street, zipCity];
  };
  const removeAddressModalProps = {
    ...removeAddressModal.fields,
    showModal: showRemoveAddressModal,
    onModalClose: evt => {
      setAddressToRemove(null);
      setShowRemoveAddressModal(false);
    },
    onSubmit: async evt => {
      await api.address.delete(null, {
        addressId: addressToRemove.value
      });
      removeAddress(addressToRemove);
      setAddressToRemove(null);
      setShowRemoveAddressModal(false);
    }
  };
  const onEditClick = (evt, option) => {
    evt.stopPropagation();
    setEditAddress(option);
    setSelectedAddress(option);
  };
  const onRemoveClick = (evt, option) => {
    evt.stopPropagation();
    setAddressToRemove(option);
    setShowRemoveAddressModal(true);
  };
  const addAddressFormProps = {
    fields,
    ref: addAddressRef,
    address: defaultAddress,
    savable: availableAddressList.length > 1,

    onSaveSuccess: async address => {
      setEditAddress(null);

      await addAddress(address);
      setAddressEditing(false);
    }
  };
  const ctaChangeAddressProps = ctaNameChange.value && {
    buttonText: ctaNameChange.value,
    ctaType: ctaTypeChange,
    colorScheme: colorSchemeChange,
    iconName: iconNameChange,
    callback(evt) {
      setAddressEditing(true);
    }
  };
  const shippingLayout = option => {
    if (option.address) {
      if (editAddress === option) {
        const editAddressFormProps = {
          fields,
          ref: editAddressRef,
          address: option.address,
          onSaveSuccess() {
            setSelectedAddress(editAddress);
            updateAddress(editAddress);
            setEditAddress(null);
            setAddressEditing(false);
          }
        };
        return (
          <div className="edit-address-block" key={generateReactKey()} onClick={e => e.stopPropagation()}>
            <p className="label">Edit Address</p>
            <ShippingInfoForm {...editAddressFormProps} />
          </div>
        );
      } else {
        return (
          <div className="saved-address-block" key={generateReactKey()}>
            <div className="body-container">
              {formatSavedAddress(option.address).map(row => {
                return (
                  <p className="content-row" key={generateReactKey()}>
                    {row}
                  </p>
                );
              })}
            </div>
            <div className="icons">
              <Icon name="Edit" colorScheme="outline-green" className="icon --visible" onClick={evt => onEditClick(evt, option)} />
              {selectedAddress !== option && availableAddressList.length > 1 && option.address.defaultFlag != "1" && <Icon name="Delete" colorScheme="outline-green" className="icon --visible" onClick={evt => onRemoveClick(evt, option)} />}
            </div>
          </div>
        );
      }
    } else {
      return <p className="label">{option.label}</p>;
    }
  };
  if (visible) {
    return (
      <div className="shipping-info" ref={componentRoot}>
        <div className="title">
          <Heading type={headingType} html={heading} />
        </div>
        {!addressEditing && selectedAddress && <AddressInformation className="default-address" address={selectedAddress.address} />}
        {isLoggedIn() && addressEditing && availableAddressList.length > 0 && <RadioButtonGroup options={availableAddressList} html={shippingLayout} alignment="top" onChange={onAddressChange} selectedOption={selectedAddress} />}
        {(!isLoggedIn() || (selectedAddress && selectedAddress.value === "new") || availableAddressList.length === 0) && <ShippingInfoForm {...addAddressFormProps} />}
        {formError && <div className="failed-message">{formError}</div>}
        {!addressEditing && availableAddressList.length > 0 && (
          <div className="cta-edit-address">
            <SuperButton {...ctaChangeAddressProps} />
          </div>
        )}
        <AccountRemoveAddressModal {...removeAddressModalProps} />
      </div>
    );
  } else {
    return null;
  }
});
ShippingInfo.displayName = "ShippingInfo";
const mapStateToProps = state => ({
  user: selectUserState(state),
  shippingFormData: shippingInfoSelector(state),
  staffInfo: staffInfoSelector(state)
});
export default connect(mapStateToProps, null, null, { forwardRef: true })(ShippingInfo);
